import React, { useEffect, useMemo, useState } from "react";
import { Copy, CheckCircle, Gift, Share2, Trophy } from "lucide-react";
import { toast } from "sonner";

/**
 * ReferralRewardCard — iter216
 *
 * Shown after a successful lead-magnet signup. Displays the reader's
 * personal share link plus a live 0/3 progress bar. When they hit 3
 * signups they unlock the Spring 2027 guide early — the milestone email
 * fires from the backend, and this card just switches to the "unlocked"
 * state.
 *
 * Props:
 *   referral: {code, share_url, count, next_milestone, unlocks, milestone_label}
 *   compact:  boolean — smaller in-form variant
 */
export default function ReferralRewardCard({ referral, compact = false }) {
  const [copied, setCopied] = useState(false);
  const [snap, setSnap] = useState(referral || null);

  // Refresh the snapshot every 30 s so the progress bar ticks up live
  // while the reader is still on the page after sharing.
  useEffect(() => {
    if (!referral?.code) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`/api/lead-magnet/referral/${referral.code}`);
        const data = await res.json();
        if (!cancelled && data?.ok) setSnap((s) => ({ ...s, ...data }));
      } catch {}
    };
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [referral?.code]);

  const shareUrl = snap?.share_url || "";
  const count = snap?.count ?? 0;
  const target = snap?.next_milestone ?? 3;
  const unlocked = (snap?.unlocks || []).includes("spring-2027-early-access");
  const pct = useMemo(() => {
    if (unlocked) return 100;
    return Math.min(100, Math.round((count / target) * 100));
  }, [count, target, unlocked]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({
        title: "The UK Secret Pitch Map — free PDF",
        text: "Free PDF I found — 200+ UK pitches you can sleep at for free (legally). Grab it before it goes private:",
        url: shareUrl,
      });
    } catch {}
  };

  if (!snap?.code) return null;

  return (
    <div
      className={`border-2 border-[#0e0e0e] bg-[#fff8f4] ${compact ? "p-4" : "p-5"}`}
      data-testid="referral-reward-card"
    >
      <div className="mono text-[10px] uppercase tracking-widest font-black text-[#ff4500] mb-1.5 inline-flex items-center gap-1.5">
        {unlocked ? <Trophy className="w-3.5 h-3.5" /> : <Gift className="w-3.5 h-3.5" />}
        {unlocked ? "Spring 2027 guide — UNLOCKED" : "Bring 3 campers · unlock the Spring guide"}
      </div>
      <h3 className={`font-display leading-tight mb-2 ${compact ? "text-lg" : "text-xl"}`}>
        {unlocked ? (
          <>You did it — early access is yours.</>
        ) : (
          <>Share your link. Unlock the next guide before anyone else.</>
        )}
      </h3>
      <p className="text-[13px] text-[#555] leading-relaxed mb-4">
        {unlocked
          ? "The Spring 2027 guide drops February — it'll land in your inbox a full week before we make it public."
          : "Every friend who grabs the map through your link counts toward the reward. No accounts, no chasing — the moment you hit 3 signups, the next season's guide is yours."}
      </p>

      <div className="flex items-stretch gap-2 mb-3">
        <input
          type="text"
          readOnly
          value={shareUrl}
          onClick={(e) => e.target.select()}
          className="flex-1 mono text-[11px] px-3 py-2 border hairline bg-white text-[#0e0e0e] truncate"
          data-testid="referral-share-url"
        />
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 bg-[#0e0e0e] text-white text-[11px] font-black uppercase tracking-widest px-3 py-2 hover:bg-[#ff4500] transition-colors"
          data-testid="referral-copy-btn"
        >
          {copied ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
        {typeof navigator !== "undefined" && "share" in navigator && (
          <button
            type="button"
            onClick={nativeShare}
            className="inline-flex items-center gap-1.5 bg-[#ff4500] text-white text-[11px] font-black uppercase tracking-widest px-3 py-2 hover:bg-[#e03d00] transition-colors"
            data-testid="referral-share-btn"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px] mono">
          <span className="uppercase tracking-widest text-[#666]">Progress</span>
          <span className="font-black text-[#0e0e0e]" data-testid="referral-progress-text">
            {unlocked ? "Unlocked · keep sharing" : `${count} / ${target} signups`}
          </span>
        </div>
        <div className="h-2 bg-white border hairline overflow-hidden">
          <div
            className={`h-full transition-[width] duration-500 ${unlocked ? "bg-[#0a7f3f]" : "bg-[#ff4500]"}`}
            style={{ width: `${pct}%` }}
            data-testid="referral-progress-bar"
          />
        </div>
      </div>
    </div>
  );
}
